import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Button, Group, Modal, Select, Stack, Text, Textarea } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@utils/api";


interface Candidate {
  user_id: string;
  name: string;
  email: string;
  plan_name: string;
  plan_version: number;
  amount: number;
  estimated: boolean;
}
interface Options {
  eligible: boolean;
  reason?: string;
  referral_id?: string;
  current_referrer_user_id?: string;
  items: Candidate[];
  has_more: boolean;
}

export const ProspectReferralAssignment = ({ prospectId }: { prospectId: string }) => {
  const { t } = useTranslation();
  const client = useQueryClient();
  const [opened, setOpened] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Candidate | null>(null);
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const options = useQuery({
    queryKey: ["account-management", "referral-assignment", prospectId, search, page],
    queryFn: async () => (await api.get<Options>(`account-management/prospects/${prospectId}/referral-assignment`, {
      params: { search, page },
    })).data,
  });
  useEffect(() => { setOpened(false); setSelected(null); setReason(""); setError(""); }, [prospectId]);
  const save = useMutation({
    mutationFn: async () => {
      const payload = { referrer_user_id: selected?.user_id, reason: reason.trim(),
        expected_referrer_user_id: options.data?.current_referrer_user_id };
      if (options.data?.referral_id) {
        await api.patch(`account-management/referrals/${options.data.referral_id}/attribution`, payload);
      } else {
        await api.post(`account-management/prospects/${prospectId}/referral-assignment`, payload);
      }
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["account-management"] });
      client.invalidateQueries({ queryKey: ["recruiters"] });
      client.invalidateQueries({ queryKey: ["dashboard", "referral-rewards"] });
      setOpened(false); setSelected(null); setReason(""); setError("");
      notifications.show({ color: "teal", message: t("referralAssignment.saved") });
    },
    onError: (err: any) => {
      const detail = err?.response?.data?.detail;
      setError(t(`referralAssignment.reasons.${detail?.code || "failed"}`, t("referralAssignment.failed")));
      options.refetch();
    },
  });
  const label = t(options.data?.referral_id ? "referralAssignment.change" : "referralAssignment.assign");
  return <Stack gap="xs" mt="md">
    <Button variant="light" onClick={() => { setOpened(true); setError(""); }}
      loading={options.isLoading} disabled={!options.data?.eligible}>{label}</Button>
    {options.isError && <Text size="xs" c="red">{t("referralAssignment.failed")}</Text>}
    {options.data?.reason && <Text size="xs" c="dimmed">{t(`referralAssignment.reasons.${options.data.reason}`)}</Text>}
    <Modal opened={opened} onClose={() => !save.isLoading && setOpened(false)} title={label} centered>
      <Stack>
        <Select searchable label={t("referralAssignment.recruiter")} searchValue={search}
          onSearchChange={value => { setSearch(value); setPage(1); }}
          value={selected?.user_id || null}
          data={Array.from(new Map([...(options.data?.items || []), ...(selected ? [selected] : [])].map(item => [item.user_id, item])).values())
            .map(item => ({ value: item.user_id, label: `${item.name} · ${item.email}` }))}
          filter={({ options: values }) => values}
          onChange={value => setSelected(options.data?.items.find(item => item.user_id === value) || null)}
          nothingFoundMessage={options.isFetching ? t("referralAssignment.loading") : t("referralAssignment.empty")} />
        <Group justify="space-between"><Button size="xs" variant="subtle" disabled={page === 1 || options.isFetching}
          onClick={() => setPage(page - 1)}>{t("referralAssignment.previous")}</Button>
          <Button size="xs" variant="subtle" disabled={!options.data?.has_more || options.isFetching}
            onClick={() => setPage(page + 1)}>{t("referralAssignment.next")}</Button></Group>
        {selected && <Alert color="violet"><Text size="sm">{selected.plan_name} · v{selected.plan_version}</Text>
          <Text fw={600}>{selected.amount.toLocaleString()} CFA · {t(selected.estimated ? "referralAssignment.estimated" : "referralAssignment.confirmed")}</Text></Alert>}
        <Textarea required label={t("referralAssignment.reason")} value={reason} maxLength={500}
          onChange={event => setReason(event.currentTarget.value)} />
        <Text size="sm" c="dimmed">{t("referralAssignment.explanation")}</Text>
        {error && <Alert color="red">{error}</Alert>}
        <Group justify="flex-end"><Button variant="default" disabled={save.isLoading} onClick={() => setOpened(false)}>{t("referralAssignment.cancel")}</Button>
          <Button loading={save.isLoading} disabled={!selected || reason.trim().length < 3 || !options.data?.eligible || options.isFetching || selected.user_id === options.data?.current_referrer_user_id}
            onClick={() => save.mutate()}>{t("referralAssignment.save")}</Button></Group>
      </Stack>
    </Modal>
  </Stack>;
};
